import { useEffect, useId, useState } from 'react';

import { Button, Icon, IconButton } from '@ds/core';
import { Input, Select, Textarea } from '@ds/forms';
import { dataEHoraPorExtenso, dataSimples } from '@/comuns/formato';
import type { Convenio, FichaDoPaciente, Profissional } from '@/data/tipos';
import type { CampoDaFicha as Campo } from './campos';

/** Nome comparável: a ficha escreve "Dra. Ana Prado"; a equipe, às vezes, "Ana Prado". */
function chaveDeNome(nome: string): string {
    return nome.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/^dra?\.?\s+/, '').trim();
}

export interface CampoDaFichaProps {
    campo: Campo;
    ficha: FichaDoPaciente;
    convenios: Convenio[];
    /** A equipe, para o campo de profissional responsável. */
    equipe?: Profissional[];
    editando: boolean;
    primeiro: boolean;
    onEditar: () => void;
    onCancelar: () => void;
    onSalvar: (valor: string) => Promise<void> | void;
}

export function CampoDaFicha({ campo, ficha, convenios, equipe = [], editando, primeiro, onEditar, onCancelar, onSalvar }: CampoDaFichaProps) {
    const valor = String(ficha[campo.chave] ?? '');
    /* O profissional se lê por nome e se grava por id: o rascunho é o id. */
    const valorDoCampo = campo.equipe ? (equipe.find((p) => chaveDeNome(p.nome) === chaveDeNome(valor))?.id ?? '') : valor;
    const [rascunho, setRascunho] = useState(valorDoCampo);
    const [salvando, setSalvando] = useState(false);
    const idCampo = useId();

    useEffect(() => {
        setRascunho(valorDoCampo);
    }, [valorDoCampo, editando]);

    /* O campo de plano lê o cadastro vivo: plano desativado sai da lista sem
       mexer no código da ficha. */
    const opcoes = campo.convenio
        ? convenios.map((c) => ({ value: c.id, label: `${c.nome} · ${c.tipo}` }))
        : campo.equipe
            ? equipe.map((p) => ({ value: p.id, label: p.nome }))
            : campo.opcoes;

    const vazio = valor === '';

    const exibicao = (): string => {
        if (vazio) return '—';
        if (campo.equipe) return valor;
        if (opcoes) return opcoes.find((o) => o.value === valor)?.label ?? valor;
        if (campo.chave === 'atualizadoEm') return `${dataEHoraPorExtenso(valor)} · ${ficha.atualizadoPor}`;
        if (campo.tipo === 'date') return dataSimples(valor);
        return valor;
    };

    const salvar = async () => {
        setSalvando(true);
        try {
            await onSalvar(rascunho);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '13px 0', borderTop: primeiro ? 'none' : '1px solid var(--border-subtle)' }}>
            <Icon
                name={campo.icone} size={16}
                style={{ color: campo.alerta && !vazio ? 'var(--warning-strong)' : 'var(--neutral-500)', marginTop: 2, flex: '0 0 auto' }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
                <label
                    htmlFor={editando ? idCampo : undefined}
                    style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}
                >
                    {campo.rotulo}
                </label>

                {editando ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 6 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                            {campo.tipo === 'textarea' ? (
                                <Textarea id={idCampo} value={rascunho} onChange={setRascunho} rows={3} />
                            ) : opcoes ? (
                                <Select id={idCampo} value={rascunho} onChange={setRascunho} options={opcoes} />
                            ) : (
                                <Input id={idCampo} type={campo.tipo === 'date' ? 'date' : campo.tipo} value={rascunho} onChange={setRascunho} />
                            )}
                        </span>
                        <Button size="sm" onClick={() => void salvar()} loading={salvando}>Salvar</Button>
                        <Button size="sm" variant="ghost" onClick={onCancelar}>Cancelar</Button>
                    </div>
                ) : (
                    <span
                        style={{
                            display: 'block', marginTop: 3, fontFamily: 'var(--font-ui)', fontSize: 13.5, lineHeight: '20px',
                            color: vazio ? 'var(--text-tertiary)' : campo.alerta ? 'var(--warning-strong)' : 'var(--text-primary)',
                            whiteSpace: campo.tipo === 'textarea' ? 'pre-wrap' : 'normal',
                        }}
                    >
                        {exibicao()}
                    </span>
                )}
            </div>

            {!editando && !campo.somenteLeitura && (
                <IconButton size="sm" icon={<Icon name="pencil" size={14} />} label={`Editar ${campo.rotulo}`} onClick={onEditar} />
            )}
        </div>
    );
}
